import React, { useState, useEffect, useRef, useCallback } from 'react';
import glossary, { searchGlossary, getCategories, filterByCategory } from '../data/glossary';
import './Dictionary.css';

function Dictionary({ isOpen, onClose }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [expandedTerm, setExpandedTerm] = useState(null);
  const [results, setResults] = useState(glossary);
  const searchInputRef = useRef(null);
  const modalRef = useRef(null);

  const categories = ['All', ...getCategories()];

  // Filter results based on search and category
  useEffect(() => {
    let filtered = searchQuery ? searchGlossary(searchQuery) : glossary;
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(item => item.category === selectedCategory);
    }
    setResults(filtered);
  }, [searchQuery, selectedCategory]);

  // Focus search input when modal opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current.focus(), 100);
    }
    if (isOpen) {
      setSearchQuery('');
      setSelectedCategory('All');
      setExpandedTerm(null);
    }
  }, [isOpen]);

  // Handle escape key
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleKeyDown]);

  // Handle click outside
  const handleBackdropClick = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      onClose();
    }
  };

  const toggleTerm = (term) => {
    setExpandedTerm(expandedTerm === term ? null : term);
  };

  if (!isOpen) return null;

  return (
    <div className="dictionary-overlay" onClick={handleBackdropClick}>
      <div className="dictionary-modal" ref={modalRef}>
        <div className="dictionary-header">
          <h2>Finance Dictionary</h2>
          <button className="close-btn" onClick={onClose} aria-label="Close">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="dictionary-search">
          <div className="search-input-wrapper">
            <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search terms (e.g., P/E, DCF, margin of safety...)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="clear-btn" onClick={() => setSearchQuery('')}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        <div className="dictionary-categories">
          {categories.map(category => (
            <button
              key={category}
              className={`category-btn ${selectedCategory === category ? 'active' : ''}`}
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>

        <div className="dictionary-content">
          {results.length === 0 ? (
            <div className="no-results">
              <p>No terms found for "{searchQuery}"</p>
              <p className="hint">Try different keywords or browse by category</p>
            </div>
          ) : (
            <div className="terms-list">
              {results.map((item, index) => (
                <div
                  key={index}
                  className={`term-card ${expandedTerm === item.term ? 'expanded' : ''}`}
                >
                  <div
                    className="term-header"
                    onClick={() => toggleTerm(item.term)}
                  >
                    <div className="term-title">
                      <h3>{item.term}</h3>
                      {item.aka && item.aka.length > 0 && (
                        <span className="term-aliases">
                          ({item.aka.join(', ')})
                        </span>
                      )}
                    </div>
                    <div className="term-meta">
                      <span className="term-category">{item.category}</span>
                      <svg
                        className={`expand-icon ${expandedTerm === item.term ? 'rotated' : ''}`}
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M6 9l6 6 6-6" />
                      </svg>
                    </div>
                  </div>

                  <div className="term-preview">
                    <p>{item.definition}</p>
                  </div>

                  {expandedTerm === item.term && (
                    <div className="term-details">
                      {item.formula && (
                        <div className="detail-section">
                          <h4>Formula</h4>
                          <code>{item.formula}</code>
                        </div>
                      )}

                      {item.interpretation && (
                        <div className="detail-section">
                          <h4>How to Interpret</h4>
                          <p>{item.interpretation}</p>
                        </div>
                      )}

                      {item.example && (
                        <div className="detail-section">
                          <h4>Example</h4>
                          <p className="example-text">{item.example}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="dictionary-footer">
          <span className="results-count">{results.length} term{results.length !== 1 ? 's' : ''}</span>
          <span className="keyboard-hint">Press <kbd>Esc</kbd> to close</span>
        </div>
      </div>
    </div>
  );
}

export default Dictionary;
